import React, { useEffect } from "react";

import clsx from "clsx";
import styled, { css, ThemeProps, DefaultTheme } from "styled-components";

import { Button } from "~/components/Button";
import CrossIcon from "~/components/icons/Cross";
import { Text } from "~/components/Text";
import { from } from "~/styles/theme";
import * as Bulma from "~/types/bulma";

export const InternalContainer = styled.div.attrs<Bulma.Activable>((props) => ({
  className: clsx({
    [`modal`]: true,
    [`is-active`]: props.active,
  }),
}))<Bulma.Activable>``;

/**
 * @example
 *
 * import * as ModalDeprecated from "~/components/ModalDeprecated";
 *
 * <ModalDeprecated.Container active={false}>
 *   <ModalDeprecated.Background onClick={handleClose} />
 *   <ModalDeprecated.Content>
 *     <ModalDeprecated.Card sized="normal">
 *       <ModalDeprecated.CardHead>
 *         <ModalDeprecated.CardHeadTitle as="h1">Title</ModalDeprecated.CardHeadTitle>
 *         <ModalDeprecated.CardHeadCloseButton onClick={handleClose} />
 *       </ModalDeprecated.CardHead>
 *       <ModalDeprecated.CardBody>Body</ModalDeprecated.CardBody>
 *       <ModalDeprecated.CardFooter>Footer</ModalDeprecated.CardFooter>
 *     </ModalDeprecated.Card>
 *   </ModalDeprecated.Content>
 *   <ModalDeprecated.Close />
 * </ModalDeprecated.Container>
 */
export function Container(props: React.ComponentProps<typeof InternalContainer>) {
  const active = Boolean(props.active);

  // TODO: improve useEffect rules

  useEffect(() => {
    document.querySelector("html")?.classList.toggle("is-clipped", active);
  }, [active]);

  useEffect(() => {
    return function cleanup() {
      document.querySelector("html")?.classList.toggle("is-clipped", false); // on unmount
    };
  }, []);

  return <InternalContainer {...props} />;
}

export const Background = styled.div.attrs({
  className: clsx({
    [`modal-background`]: true,
  }),
})``;

export const Close = styled.button.attrs<Bulma.Sized>((props) => ({
  className: clsx({
    [`modal-close`]: true,
  }),
  [`aria-label`]: "close",
  [`is-${props.sized}`]: Boolean(props.sized),
}))<Bulma.Sized>``;

export const Content = styled.div.attrs({
  className: clsx({
    [`modal-content`]: true,
  }),
})``;

/**
 * Skip <Content/> when using card.
 */
export const Card = styled.section.attrs<Bulma.Sized>({
  className: clsx({
    [`modal-card`]: true,
  }),
})<Bulma.Sized>`
  --card-padding: ${(props) => {
    switch (props.sized) {
      // case "small":
      //   return `calc(0.75rem * ${1})`;
      // case "normal":
      //   return `calc(0.75rem * ${2})`;
      // case "medium":
      //   return `calc(0.75rem * ${3})`;
      default:
        return `calc(0.75rem * ${4})`;
    }
  }};

  ${from.mobile(css`
    width: 500px;
  `)}
  ${from.tablet(css`
    width: ${(props: Bulma.Sized & ThemeProps<DefaultTheme>) => {
      switch (props.sized) {
        case "small":
          return "500px";
        default:
          return props.theme.breakpoints.tablet;
      }
    }};
  `)}
  ${from.desktop(css`
    width: ${(props: Bulma.Sized & ThemeProps<DefaultTheme>) => {
      switch (props.sized) {
        case "small":
          return "500px";
        case "normal":
          return props.theme.breakpoints.tablet;
        default:
          return props.theme.breakpoints.desktop;
      }
    }};
  `)}
  ${from.widescreen(css`
    width: ${(props: Bulma.Sized & ThemeProps<DefaultTheme>) => {
      switch (props.sized) {
        case "small":
          return "500px";
        case "normal":
          return props.theme.breakpoints.tablet;
        case "medium":
          return props.theme.breakpoints.desktop;
        default:
          return props.theme.breakpoints.widescreen;
      }
    }};
  `)}
  ${from.fullhd(css`
    width: ${(props: Bulma.Sized & ThemeProps<DefaultTheme>) => {
      switch (props.sized) {
        case "small":
          return "500px";
        case "normal":
          return props.theme.breakpoints.tablet;
        case "medium":
          return props.theme.breakpoints.desktop;
        case "large":
          return props.theme.breakpoints.widescreen;
        default:
          return props.theme.breakpoints.fullhd;
      }
    }};
  `)}
`;

export const CardHead = styled.header.attrs({
  className: clsx({
    [`modal-card-head`]: true,
  }),
})`
  padding: var(--card-padding) var(--card-padding) 0 var(--card-padding);
`;
export const CardHeadTitle = styled(Text).attrs({
  className: clsx({
    [`modal-card-title`]: true,
  }),
})``;
export const CardHeadCloseButton = styled((props) => (
  <Button {...props}>
    <CrossIcon />
  </Button>
)).attrs({
  [`aria-label`]: "close",
})``;
CardHeadCloseButton.defaultProps = {
  kind: "text",
};

export const CardBody = styled.div.attrs({
  className: clsx({
    [`modal-card-body`]: true,
  }),
})<{ empty?: boolean }>`
  padding: var(--card-padding) var(--card-padding) var(--card-padding) var(--card-padding);

  ${(props) =>
    props.empty &&
    css`
      padding: 0;
      height: calc(0.75rem * 4);
    `}
`;

export const CardFooter = styled.footer.attrs({
  className: clsx({
    [`modal-card-foot`]: true,
  }),
})`
  padding: calc(var(--card-padding) / 2) var(--card-padding);
  &&& {
    background-color: ${(props) => props.theme.colors.white};
    /* CTA shadow */
    box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.25);
  }
`;
